import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import DecisionMetrics from "@/components/DecisionMetrics";
import FinancialScenarios from "@/components/FinancialScenarios";
import { SaveReport } from "@/components/SavedReports";
import { useSession } from "@/components/Account";

export default function OwnershipScenario() {
  const {
    user,
    financialDraft,
    ownershipContext,
    setOwnershipContext,
  } = useSession();
  const [calculations, setCalculations] = useState(
    ownershipContext?.calculations || null,
  );
  const updateFinances = useCallback(
    (finances) =>
      setOwnershipContext((current) => ({ ...current, finances })),
    [setOwnershipContext],
  );
  const updateSummary = useCallback(
    (summary) => {
      setCalculations(summary);
      setOwnershipContext((current) => ({ ...current, calculations: summary }));
    },
    [setOwnershipContext],
  );
  const context = ownershipContext;

  if (!context?.inputs)
    return (
      <section className="research-card ownership-empty">
        <span className="eyebrow">Ownership scenario</span>
        <h1>Start with a property.</h1>
        <p>
          Confirm a property and review its available evidence before adding
          your purchase and cash-flow assumptions.
        </p>
        <Link className="button-link" to="/research">Research a property</Link>
      </section>
    );

  const { inputs } = context;
  const lens = {
    invest: {
      label: "Investor ownership view",
      detail: "Rent, yield, weekly cash flow and stress tests stay prominent.",
    },
    live_in: {
      label: "Owner-occupier view",
      detail: "Rent, vacancy and management costs begin at zero, while purchase cash and repayments stay prominent.",
    },
    own_review: {
      label: "Current-owner review",
      detail: "Use your current loan, insurance, rates and maintenance figures to review ongoing ownership costs.",
    },
  }[inputs.goal] || {
    label: "Ownership view",
    detail: "Model purchase and ongoing costs from your own assumptions.",
  };
  const revisionMatches =
    financialDraft?.owner_id === user?.id &&
    financialDraft?.address === inputs.address &&
    Number(financialDraft?.price) === Number(inputs.price) &&
    financialDraft?.property_type === inputs.property_type;
  const initialInputs = revisionMatches
    ? financialDraft
    : context.finances
      ? {
          purchase: context.finances.purchase,
          cashflow: context.finances.cashflow,
        }
      : null;

  return (
    <div className="ownership-page">
      <div className="ownership-heading">
        <div>
          <span className="eyebrow">A separate workspace for your assumptions</span>
          <h1>Build your ownership scenario.</h1>
          <p>{inputs.address}</p>
          <p className="source">
            {new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(Number(inputs.price))}
            {inputs.property_type ? ` · ${inputs.property_type.replaceAll("_", " ")}` : ""}
          </p>
        </div>
        <Link to="/research">← Back to property research</Link>
      </div>
      <section className="ownership-lens" aria-label="Ownership lens">
        <span className="eyebrow">Scenario focus</span>
        <h2>{lens.label}</h2>
        <p>{lens.detail}</p>
      </section>
      <DecisionMetrics calculations={calculations} propertyType={inputs.property_type} />
      <section id="costs" aria-label="Financial scenarios">
        <FinancialScenarios
          price={inputs.price}
          propertyType={inputs.property_type}
          goal={inputs.goal}
          initialInputs={initialInputs}
          onChange={updateFinances}
          onSummary={updateSummary}
        />
      </section>
      {context.evidenceId && (
        <section className="research-card ownership-save">
          <h2>Save this property and scenario</h2>
          <SaveReport
            inputs={inputs}
            evidenceId={context.evidenceId}
            explanationId={context.explanationId}
            finances={ownershipContext.finances}
            locality={context.locality}
          />
        </section>
      )}
    </div>
  );
}
